import { useEffect, useRef, useState } from 'react';
import {
  Heading,
  Link,
  List,
  ListItem,
  PageLoader,
  PanelHeader,
  PanelSection,
  PanelTitle,
  Paragraph,
  Text,
} from '@customerio/pluma-components/react';
import { isEmptyString } from '@/utils/string';
import { useLoginFlow } from '../login-flow';

type TwoFactorEmailLinkStepProps = {
  /** Magic-link token from `?token=`, present when arriving from the email link. */
  token: string | undefined;
};

// Hardcoded sender/support addresses, matching the Ember `2fa-email-link` template.
const SUPPORT_EMAIL_HREF = 'mailto:win@customer.io';
const SENDER_EMAIL = 'win@login.customer.io';
const TWO_FACTOR_DOCS_URL = 'https://docs.customer.io/accounts-and-workspaces/two-factor-auth/#2fa-via-email-links';

export function TwoFactorEmailLinkStep({ token }: TwoFactorEmailLinkStepProps) {
  const { email, validateTwoFactorLink, resetToEmailStep } = useLoginFlow();
  const hasValidated = useRef(false);
  const [validationDone, setValidationDone] = useState(false);

  const hasToken = token != null && token !== '';

  useEffect(() => {
    if (hasToken && !hasValidated.current) {
      hasValidated.current = true;
      void validateTwoFactorLink(token).then((ok) => {
        // On success the flow redirects away; on failure fall through to the
        // "check your email / Having Trouble?" content (matches Ember, which
        // only shows the loader while the validation task is running).
        if (!ok) {
          setValidationDone(true);
        }
      });
    }
  }, [hasToken, token, validateTwoFactorLink]);

  // While validating the token from the email link, show the loader (Ember:
  // `token && validateTwoFactorAuthLinkToken.isRunning`).
  if (hasToken && !validationDone) {
    return (
      <PanelSection>
        <PageLoader label="Logging you in…" />
      </PanelSection>
    );
  }

  return (
    <>
      <PanelHeader>
        <div>
          <PanelTitle level="1" mb="100">
            Welcome back!
          </PanelTitle>
          <Paragraph>
            Not this account?{' '}
            <Link onClick={resetToEmailStep} data-test-login-form-use-different-email>
              Use a different email
            </Link>
          </Paragraph>
        </div>
      </PanelHeader>
      <PanelSection>
        <Heading level="2" mb="100">
          Please check your email
        </Heading>
        <Paragraph mb="200">
          {isEmptyString(email) ? (
            'We sent an email to you. '
          ) : (
            <>
              We sent an email to you at <Text color="active">{email}</Text>.{' '}
            </>
          )}
          Click on the link to finish signing in.
        </Paragraph>

        <Heading level="2" color="bold" mb="100">
          Having Trouble?
        </Heading>
        <List mb="200">
          <ListItem>Do we have your email address correct?</ListItem>
          <ListItem>
            You'll need to click the link on the same browser and device you started the login process on.
          </ListItem>
          <ListItem>
            The email will be from{' '}
            <Link href={SUPPORT_EMAIL_HREF} variant="secondary" isExternal>
              {SENDER_EMAIL}
            </Link>{' '}
            with the subject line "Finish logging into Customer.io".
          </ListItem>
          <ListItem>Don't forget to check Spam or your Junk folder if it's not in your inbox.</ListItem>
        </List>

        <Paragraph>
          Still not working? Our apologies. We have a couple more suggestions in our{' '}
          <Link href={TWO_FACTOR_DOCS_URL} variant="secondary" isExternal>
            documentation
          </Link>{' '}
          or you can{' '}
          <Link href={SUPPORT_EMAIL_HREF} variant="secondary" isExternal>
            contact support
          </Link>
          .
        </Paragraph>
      </PanelSection>
    </>
  );
}
